<template>
  <v-input :value="value" :rules="rulesArray" :error-messages="errorMessages" :disabled="disabled" class="d-block">
    <template #default>
      <span class="cron-field" :class="{ 'cron-field--disabled': disabled }" @click.capture="onDisabledInteraction">
        <CronVuetify
          :key="cronRenderKey"
          :periods="orderedPeriods"
          :value="value"
          @input="onInput"
          @error="onCronError"
          readonly
        />
      </span>
      {{ centralTimeZone().abbr(new Date()) }}
    </template>
  </v-input>
</template>

<script>
import moment from "moment-timezone";

export default {
  name: "CronField",
  props: {
    value: { type: String, required: true },
    rules: { type: Array, default: () => [] },
    periods: {
      type: Array,
      default: () => [
        { id: "day", text: "Day", value: ["hour"] },
        { id: "week", text: "Week", value: ["dayOfWeek", "hour"] },
        { id: "month", text: "Month", value: ["day", "hour"] }
      ]
    },
    noHourly: Boolean,
    noZeroMinute: Boolean,
    disabled: Boolean
  },
  data: () => ({
    errorMessages: [],
    rulesArray: []
  }),
  computed: {
    cronRenderKey() {
      return this.inferredPeriodId;
    },
    orderedPeriods() {
      const inferredPeriod = this.periods.find(period => period.id === this.inferredPeriodId);
      if (!inferredPeriod) {
        return this.periods;
      }
      return [...this.periods.filter(period => period.id !== inferredPeriod.id), inferredPeriod];
    },
    inferredPeriodId() {
      const parts = String(this.value || "")
        .trim()
        .split(/\s+/);
      if (parts.length < 5) {
        return this.periods[this.periods.length - 1]?.id;
      }

      const [, hour, day, month, dayOfWeek] = parts;
      if (month && month !== "*") {
        return "month";
      }
      if (day && day !== "*" && day !== "?") {
        return "month";
      }
      if (dayOfWeek && dayOfWeek !== "*" && dayOfWeek !== "?") {
        return "week";
      }
      if (hour && hour !== "*") {
        return "day";
      }
      return this.periods[this.periods.length - 1]?.id;
    }
  },
  methods: {
    centralTimeZone() {
      return moment.tz.zone("America/Chicago");
    },
    onInput(obj) {
      if (this.disabled) {
        return;
      }
      obj = this.noZeroMinute ? this.normalizeToMinuteZero(obj) : obj;
      this.$emit("input", obj);
    },
    onDisabledInteraction(event) {
      if (!this.disabled) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    },
    onCronError(msg) {
      // library emits 'error' for syntactically invalid cron;
      // show it alongside your rules if needed
      this.errorMessages = msg ? [msg] : [];
    },
    normalizeToMinuteZero(v) {
      const parts = String(v || "")
        .trim()
        .split(/\s+/);
      if (parts.length < 5) return v; // let component handle syntax errors
      parts[0] = "0"; // force minute=0
      return parts.slice(0, 5).join(" ");
    },
    validateNoHourly(v) {
      const p = String(v || "")
        .trim()
        .split(/\s+/);
      return (p.length >= 5 && p[1] !== "*") || "Every-hour schedules are not allowed.";
    },
    resetRulesArray() {
      this.rulesArray = [];
      if (this.rules) {
        this.rulesArray = this.rulesArray.concat(this.rules);
      }
      if (this.noHourly) {
        this.rulesArray.push(this.validateNoHourly);
      }
    }
  },

  mounted() {
    this.resetRulesArray();
  }
};
</script>

<style scoped>
.cron-field {
  display: inline-block;
}

.cron-field--disabled {
  opacity: 0.6;
  pointer-events: none;
}
</style>
